import { debounce } from '@/service/debounce.js';

export const identifyOverlappingChords = debounce((container) => {
    const chordsElements = container.querySelectorAll('.chord-item')
    chordsElements.forEach(chord => {
        const chordPosition = chord.getBoundingClientRect()
        const overlappingChords = Array.from(chordsElements).filter(c => {
            const cPosition = c.getBoundingClientRect()
            return cPosition.left < chordPosition.right && cPosition.right > chordPosition.left && cPosition.top < chordPosition.bottom && cPosition.bottom > chordPosition.top
        })
        if (overlappingChords.length > 1) {
            const firstChord = overlappingChords[0]
            firstChord.innerHTML = firstChord.innerHTML + overlappingChords.slice(1)
                .map(c => ' ' + c.textContent).join(' ')
            overlappingChords.slice(1).forEach(c => {
                c.remove()
            })
        }
    })
}, 300)

export const preprocessHtml = (html) => {
    if (!html) return html;
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const hasChanges = splitMergedChords(doc.body);
    return hasChanges ? doc.body.innerHTML : html;
};

export const splitMergedChords = (container) => {
    const chordsElements = Array.from(container.querySelectorAll('.chord-item, .chord-span'));
    let hasChanges = false;

    // Step 1: Sanitize existing single-chord elements whose data-chord/chord attributes contain multi-chord strings.
    // In legacy data, a single span like <span class="chord-span">E4</span> might have data-chord="E4 E E4 E A".
    // We clean those attributes to match the single text content "E4".
    chordsElements.forEach(chord => {
        let textContent = (chord.textContent || '').trim();
        if (textContent && !/\s/.test(textContent)) {
            const parentContainer = chord.closest('.chord-container') || chord.closest('sup.chord-sup');
            if (parentContainer && parentContainer.getAttribute('data-chord') !== textContent) {
                parentContainer.setAttribute('data-chord', textContent);
                if (parentContainer.hasAttribute('chord')) parentContainer.setAttribute('chord', textContent);
                hasChanges = true;
            }
            if (chord.getAttribute('data-chord') !== textContent) {
                chord.setAttribute('data-chord', textContent);
                if (chord.hasAttribute('chord')) chord.setAttribute('chord', textContent);
                hasChanges = true;
            }
        }
    });

    // Step 2: Process multi-chord elements that need grouping or splitting
    const processed = new Set();

    chordsElements.forEach(chord => {
        if (processed.has(chord)) return;

        let textContent = (chord.textContent || '').trim();
        // Add spaces between glued chords (e.g. "F#mB" -> "F#m B", "EF#AE" -> "E F# A E")
        textContent = textContent.replace(/([A-Za-z0-9#])(?=[A-G])/g, '$1 ');
        
        if (/\s/.test(textContent)) {
            const parts = textContent.split(/\s+/).filter(p => p.trim() !== '');
            const spaces = textContent.match(/\s+/g) || [];
            
            if (parts.length > 1) {
                const parentContainer = chord.closest('.chord-container') || chord.closest('sup.chord-sup') || chord;
                
                // Find parent section (blockquote or container) to group all related chord nodes
                const section = parentContainer.closest('.custom-blockquote') || container;
                
                // Find all chord items in this section with the exact same multi-chord text
                const sectionChordItems = Array.from(section.querySelectorAll('.chord-item, .chord-span'))
                    .filter(el => {
                        const t = (el.textContent || '').trim().replace(/([A-Za-z0-9#])(?=[A-G])/g, '$1 ');
                        return t === textContent;
                    });
                
                const group = sectionChordItems.map(el => el.closest('.chord-container') || el.closest('sup.chord-sup') || el);
                
                // Remove duplicates in group while preserving order
                const uniqueGroup = Array.from(new Set(group));

                // If we found a group of identical containers in this section whose count is a multiple of parts count:
                if (uniqueGroup.length > 0 && uniqueGroup.length % parts.length === 0) {
                    uniqueGroup.forEach((containerNode, idx) => {
                        const part = parts[idx % parts.length];
                        const chordSpan = containerNode.querySelector ? (containerNode.querySelector('.chord-item, .chord-span') || containerNode) : containerNode;
                        
                        if (containerNode.tagName === 'SUP') {
                            containerNode.setAttribute('data-chord', part);
                        } else if (containerNode.classList && containerNode.classList.contains('chord-container')) {
                            containerNode.setAttribute('chord', part);
                            containerNode.setAttribute('data-chord', part);
                        }
                        
                        if (chordSpan) {
                            chordSpan.setAttribute('data-chord', part);
                            chordSpan.setAttribute('chord', part);
                            chordSpan.textContent = part;
                            processed.add(chordSpan);
                        }
                    });
                    hasChanges = true;
                } else {
                    // Normal split: replace single parent container with multiple containers
                    const fragment = document.createDocumentFragment();
                    parts.forEach((part, index) => {
                        const newContainer = document.createElement('sup');
                        newContainer.className = 'chord-sup';
                        newContainer.setAttribute('data-type', 'chord');
                        newContainer.setAttribute('data-chord', part);
                        
                        const newChord = document.createElement('span');
                        newChord.className = 'chord-span';
                        newChord.setAttribute('contenteditable', 'false');
                        newChord.setAttribute('draggable', 'true');
                        newChord.setAttribute('data-chord', part);
                        newChord.textContent = part;
                        newContainer.appendChild(newChord);

                        fragment.appendChild(newContainer);
                        
                        if (index < spaces.length) {
                            fragment.appendChild(document.createTextNode(spaces[index]));
                        } else if (index < parts.length - 1) {
                            fragment.appendChild(document.createTextNode(' '));
                        }
                    });

                    if (parentContainer && parentContainer.parentNode) {
                        parentContainer.parentNode.insertBefore(fragment, parentContainer);
                        parentContainer.remove();
                    } else if (chord.parentNode) {
                        chord.parentNode.insertBefore(fragment, chord);
                        chord.remove();
                    }

                    hasChanges = true;
                }
            }
        }
    });

    return hasChanges;
};

export const adjustChordSpacing = debounce(() => {
    /*
    if (!editor || !editor.view) return;
    const container = editor.view.dom;

    // To measure natural overlap without triggering ProseMirror's MutationObserver,
    // we hide the spacers using a CSS class on the body.
    document.body.classList.add('measure-chords');

    // Force reflow to ensure the layout is updated before measuring
    void container.offsetHeight;

    const spacerMap = new Map();

    const chordsElements = container.querySelectorAll('.chord-item');
    for (let i = 0; i < chordsElements.length - 1; i++) {
        const chord = chordsElements[i];
        
        // Skip chords that are inside a custom section block
        if (chord.closest('[data-custom-section]')) continue;
        
        const nextChord = chordsElements[i + 1];
        if (nextChord.closest('[data-custom-section]')) continue;
        
        const chordRect = chord.getBoundingClientRect();
        const nextRect = nextChord.getBoundingClientRect();

        // Skip hidden elements (0 rects)
        if (chordRect.width === 0 || nextRect.width === 0) continue;

        // Check if they are on the same line (vertically close)
        if (Math.abs(chordRect.top - nextRect.top) < 20) {
            // Allow a small gap
            const requiredGap = 5;
            if (chordRect.right + requiredGap > nextRect.left) {
                const overlap = (chordRect.right + requiredGap) - nextRect.left;
                const parentContainer = chord.closest('.chord-container');
                if (parentContainer) {
                    const { beforeChar, afterChar } = getSurroundingText(parentContainer);
                    const isWithinWord = isWordChar(beforeChar) && isWordChar(afterChar);
                    const spacerChar = isWithinWord ? '_' : ' ';

                    const charWidth = getCharWidth(parentContainer, spacerChar);
                    const numChars = Math.ceil(overlap / charWidth);

                    if (numChars > 0) {
                        const spacer = spacerChar.repeat(numChars);
                        const id = parentContainer.getAttribute('data-id');
                        if (id) spacerMap.set(id, spacer);
                    }
                }
            }
        }
    }

    // Remove the measurement class
    document.body.classList.remove('measure-chords');

    // Apply the computed spacers to the TipTap state
    let tr = editor.state.tr;
    let hasUpdates = false;

    // First find existing spacers in state
    editor.state.doc.descendants((node, pos) => {
        if (node.isText && node.marks) {
            node.marks.forEach(mark => {
                if (mark.type.name === 'chordClass') {
                    const id = mark.attrs['data-id'];
                    const currentSpacer = mark.attrs['data-spacer'];
                    const newSpacer = spacerMap.has(id) ? spacerMap.get(id) : null;
                    
                    if (currentSpacer !== newSpacer) {
                        const newAttrs = { ...mark.attrs };
                        if (newSpacer) {
                            newAttrs['data-spacer'] = newSpacer;
                        } else {
                            delete newAttrs['data-spacer'];
                        }
                        
                        tr = tr.removeMark(pos, pos + node.nodeSize, mark.type);
                        tr = tr.addMark(pos, pos + node.nodeSize, mark.type.create(newAttrs));
                        hasUpdates = true;
                    }
                }
            });
        }
    });

    if (hasUpdates) {
        tr.setMeta('isChordSpacing', true); // Prevent infinite loop in onUpdate
        editor.view.dispatch(tr);
    }
    */
}, 100);

// const charWidthCache = new Map();

/* Commented out as it's not used while adjustChordSpacing is disabled
function getCharWidth(element, char = '_') {
    const font = window.getComputedStyle(element).font || '16px sans-serif';
    const cacheKey = font + '_' + char;
    if (charWidthCache.has(cacheKey)) {
        return charWidthCache.get(cacheKey);
    }
    const testSpan = document.createElement('span');
    testSpan.style.visibility = 'hidden';
    testSpan.style.position = 'absolute';
    testSpan.style.whiteSpace = 'pre';
    testSpan.style.font = font;
    testSpan.textContent = char;
    document.body.appendChild(testSpan);
    const width = testSpan.getBoundingClientRect().width || 8;
    document.body.removeChild(testSpan);
    charWidthCache.set(cacheKey, width);
    return width;
}
*/

export function isWordChar(char) {
    if (!char) return false;
    try {
        return /[\p{L}\p{N}\-_]/u.test(char);
    } catch (e) {
        return /[a-zA-Z0-9\-_]/.test(char) || char.charCodeAt(0) > 127;
    }
}

function getLastTextOfSubtree(node) {
    if (node.nodeType === 3) { // Text node
        return node.textContent.replace(/\u200B/g, '');
    }
    if (node.nodeType === 1) { // Element node
        if (node.classList && (node.classList.contains('chord-container') || node.classList.contains('chord-sup'))) return '';
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
            const text = getLastTextOfSubtree(node.childNodes[i]);
            if (text) return text;
        }
    }
    return '';
}

function getFirstTextOfSubtree(node) {
    if (node.nodeType === 3) { // Text node
        return node.textContent.replace(/\u200B/g, '');
    }
    if (node.nodeType === 1) { // Element node
        if (node.classList && (node.classList.contains('chord-container') || node.classList.contains('chord-sup'))) return '';
        for (let i = 0; i < node.childNodes.length; i++) {
            const text = getFirstTextOfSubtree(node.childNodes[i]);
            if (text) return text;
        }
    }
    return '';
}

function isBlockElement(el) {
    if (!el || el.nodeType !== 1) return false;
    try {
        const display = window.getComputedStyle(el).display;
        if (display === 'block' || display === 'flex' || display === 'grid') return true;
    } catch (e) {
        // ignore
    }
    return el.tagName === 'P' || el.tagName === 'DIV' || el.tagName === 'SECTION' || el.tagName === 'ARTICLE';
}

export function getSurroundingText(chordContainer) {
    let beforeChar = '';
    let afterChar = '';

    // Find preceding text
    let node = chordContainer;
    while (node) {
        if (node.previousSibling) {
            node = node.previousSibling;
            if (isBlockElement(node)) {
                break;
            }
            const text = getLastTextOfSubtree(node);
            if (text) {
                beforeChar = text.slice(-1);
                break;
            }
        } else {
            node = node.parentNode;
            if (!node || node === chordContainer.ownerDocument.body || isBlockElement(node)) {
                break;
            }
        }
    }

    // Find succeeding text
    node = chordContainer;
    while (node) {
        if (node.nextSibling) {
            node = node.nextSibling;
            if (isBlockElement(node)) {
                break;
            }
            const text = getFirstTextOfSubtree(node);
            if (text) {
                afterChar = text[0];
                break;
            }
        } else {
            node = node.parentNode;
            if (!node || node === chordContainer.ownerDocument.body || isBlockElement(node)) {
                break;
            }
        }
    }

    return { beforeChar, afterChar };
}
